import { Box } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { concat, get } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from 'src/context/ApplicationContext';
import { ANTRAG_TYP, ANTRAGS_FORMULAREN, FORM_FIELDS_TYPES, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import { Field, FormStrucItem } from 'src/lib/types/types';
import {
	EmailValidation,
	GetMinMaxDecimalValidation,
	NonRequired,
	PhoneNumberValidation,
	PostalCodeValidation,
	Required,
} from 'src/lib/validation/ValidationTypes';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { getInstitutionsTypOption } from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { checkReadOnlyInUeberarbeitung, isTrue } from 'src/utils/helper';

// import Schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
let P = Schema.attributes;

const CUSTOM_PLACEHOLDER = 'n.n. - bitte im Profil angeben';

const AntragstellerGlobal: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);
	return (
		<>
			<ApplicationHeading title='Organisation' description={PLATZHALTER_TEXT} />

			<Box>
				<Grid2 container spacing={6}>
					<Grid2 sm={6}>
						{/**
						 * *************************************************************************************************************************
						 * ************************** Organisation Basis Daten (Die sind immer unabhängig vom Programm da) *************************
						 * *************************************************************************************************************************
						 */}
						<CreateForm
							formStruc={formStrucOrgaBasic(application)}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={updatedReadOnly}
						/>
					</Grid2>
					<Grid2 sm={6}>
						{/**
						 * *************************************************************************************************************************
						 * ***************************** Organisation Daten hier sind aber immer vom Programm abhängig *****************************
						 * *************************************************************************************************************************
						 */}
						<CreateForm
							formStruc={formStrucOrgaSpalte2(application)}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={updatedReadOnly}
						/>
					</Grid2>
				</Grid2>
			</Box>
		</>
	);
};

export default AntragstellerGlobal;

const formStrucOrgaBasic = (application: any): FormStrucItem[] => {
	const antragsFormular = get(application, P.Allgemein_Antragsformular.path);
	const adressZusatz = get(application, P.Antragstellende_Organisation_Adresszusatz.path, null);
	const hatKeinAbweichendenSitzList = [ANTRAGS_FORMULAREN.Entwicklungszusammenarbeit];
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Antragstellende_Organisa_0074063902_licher_Name_Der_Organisation, disabled: true },
				{
					...P.Antragstellende_Organisation_Rechtsform_Der_Organisation,
					sortAlphabetically: true,
					disabled: true,
					placeholder: CUSTOM_PLACEHOLDER,
				},
				{ ...P.Antragstellende_Organisation_Strasse_Und_Hausnummer, disabled: true },
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		...(adressZusatz ?
			[
				{
					fields: [{ ...P.Antragstellende_Organisation_Adresszusatz, disabled: true }],
					userProps: { direction: 'column', columnToDivide: 1 },
				},
			]
		:	[]),
		{
			fields: [
				{
					...P.Antragstellende_Organisation_PLZ,
					disabled: true,
					validation: [{ validateFunction: Required }, { validateFunction: PostalCodeValidation }],
					fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '30%' : null,
				},
				{
					...P.Antragstellende_Organisation_Ort,
					disabled: true,
					fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '70%' : null,
				},
			],
			userProps: { columnToDivide: 2 },
		},
		...(!hatKeinAbweichendenSitzList.includes(antragsFormular) ?
			[
				{
					fields: [
						{
							...P.Antragstellende_Organisation_Abweichender_Sitz,
							...(antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium && { label: 'Abweichender Sitz des Unternehmens' }),
						},
					],
					userProps: { columnToDivide: 1 },
				},
			]
		:	[]),
		...(isTrue(get(application, P.Antragstellende_Organisation_Abweichender_Sitz.path)) ?
			[
				{
					fields: [
						{
							...P.Antragstellende_Organisa_0234235106_resse_Strasse_Und_Hausnummer,
							label: 'Abweichende Straße und Hausnummer',
						},
					],
					userProps: { columnToDivide: 1 },
				},
			]
		:	[]),
		...(isTrue(get(application, P.Antragstellende_Organisation_Abweichender_Sitz.path)) ?
			[
				{
					fields: [
						{
							...P.Antragstellende_Organisation_Abweichender_Sitz_Adresse_PLZ,
							label: 'Abweichende PLZ',
							type: 'integer',
							maxDigit: 5,
							validation: [{ validateFunction: Required }, { validateFunction: PostalCodeValidation }],
							fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '30%' : null,
						},
						{
							...P.Antragstellende_Organisation_Abweichender_Sitz_Adresse_Ort,
							label: 'Abweichender Ort',
							fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '70%' : null,
						},
					],
				},
			]
		:	[]),
		{
			fields: [
				{
					...P.Antragstellende_Organisation_Telefonnummer,
					disabled: true,
					validation: [{ validateFunction: Required }, { validateFunction: PhoneNumberValidation }],
					fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '30%' : null,
				},
				{
					...P.Antragstellende_Organisation_E_Mail_Adresse,
					disabled: true,
					validation: [{ validateFunction: Required }, { validateFunction: EmailValidation }],
					fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '70%' : null,
				},
			],
			userProps: { columnToDivide: 2 },
		},
	];

	return struc;
};

/**
 * ********************************************************************************************************************************************
 * ********************** Hier werden die Felder abhängig vom Förderprogramm unterschiedlich zurückgegeben ************************************
 * ********************************************************************************************************************************************
 * @param application | OBJECT: Gesamten Antrag als JSON
 * @returns | FORMSTRUCITEM Als Array.
 */
const formStrucOrgaSpalte2 = (application: any) => {
	const antragsFormular = get(application, P.Allgemein_Antragsformular.path);
	const defaultFields: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Organisation_Antragsberechtigt_Als,
					type: FORM_FIELDS_TYPES.singleSelect,
					sortAlphabetically: false,
					options: getInstitutionsTypOption(application),
				},
			],
			userProps: { columnToDivide: 1 },
		},
		// "Branche" moved here so it always renders directly below "Antragsberechtigt als"
		// Exception: Entwicklungszusammenarbeit (EPZ) has no Branche field
		...(antragsFormular !== ANTRAGS_FORMULAREN.Entwicklungszusammenarbeit ?
			[
				{
					fields: [{ ...P.Antragstellende_Organisation_Branche }],
					userProps: { columnToDivide: 1 },
				},
			]
		:	[]),
		{
			fields: [...getSteuerNummerOrUmsatzsteuerIdStruc(application), { ...P.Antragstellende_Organisation_Gruendungsdatum, disabled: true }],
			userProps: { columnToDivide: 2 },
		},
	];

	switch (antragsFormular) {
		case ANTRAGS_FORMULAREN.EmobInvest:
			return [...defaultFields, ...getFormStrucOrgaEmobInves(application)];
		case ANTRAGS_FORMULAREN.EmobLis:
			return [...defaultFields, ...getFormStrucOrgaEmobLis(application)];
		case ANTRAGS_FORMULAREN.Mobilitaetsplaene:
		case ANTRAGS_FORMULAREN.StartUpProjekte:
		case ANTRAGS_FORMULAREN.LogistikUndMobilitaet:
			return [...defaultFields, ...getFormStrucOrgaStartUp(application)];
		case ANTRAGS_FORMULAREN.Kreativwirtschaft:
			return [...getFormStrucOrgaKreativ(application)];
		case ANTRAGS_FORMULAREN.Messefoerderung:
			return [...defaultFields, ...getFormStrucOrgaMessefoerderung(application)];
		case ANTRAGS_FORMULAREN.Entwicklungszusammenarbeit:
			return [...defaultFields, ...getFormStrucOrgaEPZ(application)];
		case ANTRAGS_FORMULAREN.Loewe:
			return [...getFormStrucOrgaLoewe(application)];
		case ANTRAGS_FORMULAREN.StartUpStipendium:
			return [...getFormStrucStartupStipendium(application)];
		default:
			return [...defaultFields, ...getFormStrucOrgaDefault(application)];
	}
};

export function getFormStrucAntragstellerGlobal(application: any) {
	let tableStruc: any = [];

	let orgaStruc: FormStrucItem[] = [];

	if (get(application, P.Allgemein_Typ.path) === ANTRAG_TYP.Antrag) {
		orgaStruc = concat(tableStruc, formStrucOrgaBasic(application), formStrucOrgaSpalte2(application));
	} else {
		orgaStruc = concat(tableStruc, formStrucOrgaBasic(application), formStrucOrgaSpalte2(application));
	}

	if (
		get(application, P.Antragstellende_Pruefung_Hinweis_Fuer_Den_Antragsteller.path) &&
		!isTrue(get(application, P.Antragstellende_Pruefung_Erledigt.path))
	) {
		return concat(orgaStruc);
	} else return orgaStruc;
}

const getFormStrucOrgaDefault = (application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Antragstellende_Organisation_Registergericht, disabled: true },
				{ ...P.Antragstellende_Organisation_Handelsregisternummer, disabled: true, validation: [{ validateFunction: NonRequired }] },
			],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [
				{ ...P.Antragstellende_Organisation_Landkreis, disabled: true, sortAlphabetically: true, placeholder: CUSTOM_PLACEHOLDER },
				{ ...P.Antragstellende_Organisation_Oeffentlicher_Auftraggeber, type: 'checkbox' },
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];

	if (get(application, P.Allgemein_Typ.path) === ANTRAG_TYP.Antrag) {
		struc.push({
			fields: [{ ...P.Antragstellende_Organisation_Angaben_Zum_Vorsteuerabzug, type: FORM_FIELDS_TYPES.radio }],
			userProps: { direction: 'column', columnToDivide: 1 },
		});
	}
	return struc;
};
const getFormStrucOrgaEmobInves = (_application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Antragstellende_Organisation_Registergericht, disabled: true },
				{ ...P.Antragstellende_Organisation_Handelsregisternummer, disabled: true, validation: [{ validateFunction: NonRequired }] },
			],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Landkreis, disabled: true, sortAlphabetically: true, placeholder: CUSTOM_PLACEHOLDER }],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [
				{ ...P.Antragstellende_Organisation_Oeffentlicher_Auftraggeber, disabled: true, type: 'checkbox' },
				{ ...P.Antragstellende_Organisation_Sektorenauftraggeber, disabled: true, type: 'checkbox' },
			],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Oe_PNV_Lizenz_Vorhanden, disabled: true, type: 'checkbox' }],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisa_0252192846_fuehrung_Von_Ausschreibungen, disabled: true, type: 'checkbox' }],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Angaben_Zum_Vorsteuerabzug, disabled: true, type: FORM_FIELDS_TYPES.radio }],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};
const getFormStrucOrgaEmobLis = (_application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [{ ...P.Antragstellende_Organisation_Landkreis, disabled: true, sortAlphabetically: true, placeholder: CUSTOM_PLACEHOLDER }],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [
				{ ...P.Antragstellende_Organisation_Oeffentlicher_Auftraggeber, disabled: true, type: 'checkbox' },
				{ ...P.Antragstellende_Organisation_Sektorenauftraggeber, disabled: true, type: 'checkbox' },
			],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisa_0252192846_fuehrung_Von_Ausschreibungen, disabled: true, type: 'checkbox' }],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Angaben_Zum_Vorsteuerabzug, disabled: true, type: FORM_FIELDS_TYPES.radio }],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};
const getFormStrucOrgaEPZ = (_application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Antragstellende_Organisation_Registergericht, disabled: true },
				{
					...P.Antragstellende_Organisation_Handelsregisternummer,
					label: 'Registernummer',
					disabled: true,
					validation: [{ validateFunction: NonRequired }],
				},
			],
			userProps: { columnToDivide: 2 },
		},
	];

	return struc;
};
const getFormStrucOrgaStartUp = (_application: any): FormStrucItem[] => {
	return [
		{
			fields: [
				{ ...P.Antragstellende_Organisation_Registergericht, disabled: true },
				{ ...P.Antragstellende_Organisation_Handelsregisternummer, disabled: true, validation: [{ validateFunction: NonRequired }] },
			],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Landkreis, disabled: true, sortAlphabetically: true, placeholder: CUSTOM_PLACEHOLDER }],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Angaben_Zum_Vorsteuerabzug, disabled: true, type: FORM_FIELDS_TYPES.radio }],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	] as FormStrucItem[];
};
const getFormStrucOrgaKreativ = (application: any): FormStrucItem[] => {
	return [
		{
			fields: [
				{
					...P.Organisation_Antragsberechtigt_Als,
					type: FORM_FIELDS_TYPES.singleSelect,
					sortAlphabetically: false,
					options: getInstitutionsTypOption(application),
				},
			],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Landkreis, disabled: true, sortAlphabetically: true, placeholder: CUSTOM_PLACEHOLDER }],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Angaben_Zum_Vorsteuerabzug, disabled: true, type: FORM_FIELDS_TYPES.radio }],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisa_0099968647_ionell_Gefoerdert_Foerderung, type: 'checkbox' }],
			userProps: { direction: 'column', columnToDivide: 2 },
		},
		isTrue(get(application, P.Antragstellende_Organisa_0099968647_ionell_Gefoerdert_Foerderung.path)) ?
			{
				fields: [
					{
						...P.Antragstellende_Organisa_0262845644_foerdert_prozentualer_Anteil,
						validation: [{ validateFunction: GetMinMaxDecimalValidation, params: [0, 100], onlyValidateOnNonEmptyValue: true }],
					},
				],
			}
		:	{},
	] as FormStrucItem[];
};
const getFormStrucOrgaMessefoerderung = (_application: any): FormStrucItem[] => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Antragstellende_Organisation_Registergericht, disabled: true },
				{ ...P.Antragstellende_Organisation_Handelsregisternummer, disabled: true, validation: [{ validateFunction: NonRequired }] },
			],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Landkreis, disabled: true }],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const getFormStrucOrgaLoewe = (application: any): FormStrucItem[] => {
	const isSkizze = get(application, P.Allgemein_Typ.path) === ANTRAG_TYP.Skizze;

	let struc: FormStrucItem[] = isSkizze ? getFormStrucOrgaLoeweSkizze(application) : getFormStrucOrgaLoeweAntrag(application);

	return struc;
};

const getFormStrucOrgaLoeweAntrag = (application: any): FormStrucItem[] => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Organisation_Antragsberechtigt_Als,
					type: FORM_FIELDS_TYPES.singleSelect,
					sortAlphabetically: false,
					options: getInstitutionsTypOption(application),
				},
			],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Anzahl_Der_Mitarbeiter }, { ...P.Antragstellende_Organisation_Jahresumsatzin }],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Branche }],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [...getSteuerNummerOrUmsatzsteuerIdStruc(application), { ...P.Antragstellende_Organisation_Gruendungsdatum, disabled: true }],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [
				{ ...P.Antragstellende_Organisation_Registergericht, disabled: true },
				{ ...P.Antragstellende_Organisation_Handelsregisternummer, disabled: true, validation: [{ validateFunction: NonRequired }] },
			],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [
				{ ...P.Antragstellende_Organisation_Landkreis, disabled: true, sortAlphabetically: true, placeholder: CUSTOM_PLACEHOLDER },
				{ ...P.Antragstellende_Organisation_Oeffentlicher_Auftraggeber, type: 'checkbox' },
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const getFormStrucOrgaLoeweSkizze = (application: any): FormStrucItem[] => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Organisation_Antragsberechtigt_Als,
					type: FORM_FIELDS_TYPES.singleSelect,
					sortAlphabetically: true,
					options: getInstitutionsTypOption(application),
				},
			],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [
				{ ...P.Antragstellende_Organisation_Anzahl_Der_Mitarbeiter, label: 'Anzahl Mitarbeitende' },
				{ ...P.Antragstellende_Organisation_Jahresumsatzin },
			],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Branche }],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [
				...getSteuerNummerOrUmsatzsteuerIdStruc(application),
				{ ...P.Antragstellende_Organisation_Gruendungsdatum, tooltip: '', disabled: true },
			],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [
				{
					...P.Antragstellende_Organisation_Handelsregisternummer,
					label: 'Registernummer',
					tooltip: '',
					disabled: true,
					validation: [{ validateFunction: NonRequired }],
				},
			],
			userProps: { columnToDivide: 2, columnGap: '1rem' },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Landkreis, disabled: true, sortAlphabetically: true, placeholder: CUSTOM_PLACEHOLDER }],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const getFormStrucStartupStipendium = (application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Organisation_Antragsberechtigt_Als,
					type: FORM_FIELDS_TYPES.singleSelect,
					sortAlphabetically: false,
					options: getInstitutionsTypOption(application),
				},
			],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Branche }],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [{ ...P.Antragstellende_Organisation_Landkreis, disabled: true, sortAlphabetically: true, placeholder: CUSTOM_PLACEHOLDER }],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [...getSteuerNummerOrUmsatzsteuerIdStruc(application)],
			userProps: { columnToDivide: 2 },
		},
	];

	return struc;
};

const getSteuerNummerOrUmsatzsteuerIdStruc = (application: any) => {
	const hasSteuerNummer = !!get(application, P.Antragstellende_Organisation_Steuernummer.path);
	const hasUmsatzsteuerId = !!get(application, P.Antragstellende_Organisation_U_St_Id_Nr.path);
	let fieldArr: Field[] = [];
	if (hasSteuerNummer) {
		fieldArr.push({ ...P.Antragstellende_Organisation_Steuernummer, disabled: true });
	} else if (hasUmsatzsteuerId) {
		fieldArr.push({ ...P.Antragstellende_Organisation_U_St_Id_Nr, disabled: true });
	}
	return fieldArr;
};
